/**
 * Bundle Builder
 * --------------
 * Custom element that powers the "bundle-builder" section.
 *
 * Features:
 *  - Toggle products in/out of the bundle by clicking the card image.
 *  - Per-product variant selector with live price updates.
 *  - Per-product quantity controls (+ / - / input).
 *  - Tiered progress based on TOTAL UNITS, not number of products.
 *  - Live totals: original price, discounted price and savings.
 *  - Optional per-tier discount codes applied through /discount/{code}.
 *  - Adds the correct quantity for every selected variant through /cart/add.js.
 *  - Opens the theme cart drawer or redirects to the cart page.
 *
 * No external dependencies.
 */

(function () {
  'use strict';

  if (customElements.get('bundle-builder')) return;

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();

      /*
       * productId -> {
       *   variantId: Number,
       *   price: Number,
       *   quantity: Number
       * }
       */
      this.selected = new Map();

      this.cards = [];
      this.cardData = new Map();
      this.isAdding = false;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;

      this.config = this.parseConfig();

      if (!this.config) return;

      this.initialized = true;

      this.cacheElements();
      this.initCards();
      this.initAddButton();
      this.update();
    }

    cacheElements() {
      this.cards = Array.from(
        this.querySelectorAll('[data-bb-card]')
      );

      this.messageEl = this.querySelector(
        '[data-bb-message]'
      );

      this.segments = Array.from(
        this.querySelectorAll('[data-bb-segment]')
      );

      this.badges = Array.from(
        this.querySelectorAll('[data-bb-badge]')
      );

      this.totalOriginalEl = this.querySelector(
        '[data-bb-total-original]'
      );

      this.totalFinalEl = this.querySelector(
        '[data-bb-total-final]'
      );

      this.savingsEl = this.querySelector(
        '[data-bb-savings]'
      );

      this.addButton = this.querySelector(
        '[data-bb-add]'
      );

      this.addButtonLabel = this.addButton
        ? this.addButton.querySelector(
            '[data-bb-add-label]'
          )
        : null;
    }

    parseConfig() {
      const configEl = this.querySelector(
        '[data-bb-config]'
      );

      if (!configEl) return null;

      try {
        const config = JSON.parse(
          configEl.textContent
        );

        config.minQty = Math.max(
          0,
          Number(config.minQty) || 0
        );

        config.maxQty = Math.max(
          config.minQty,
          Number(config.maxQty) || 0
        );

        config.tiers = Array.isArray(config.tiers)
          ? config.tiers
          : [];

        config.tiers.sort(
          (a, b) =>
            Number(a.qty) - Number(b.qty)
        );

        return config;
      } catch (error) {
        console.error(
          'BundleBuilder: invalid config JSON',
          error
        );

        return null;
      }
    }

    initCards() {
      this.cards.forEach((card) => {
        const productId =
          card.getAttribute('data-product-id');

        if (!productId) return;

        const variantsEl = card.querySelector(
          '[data-bb-variants]'
        );

        let variants = [];

        try {
          variants = variantsEl
            ? JSON.parse(variantsEl.textContent)
            : [];
        } catch (error) {
          console.warn(
            'BundleBuilder: invalid variant JSON',
            productId,
            error
          );
        }

        const isAvailable =
          card.getAttribute('data-available') ===
          'true';

        if (
          !isAvailable ||
          !variants.length
        ) {
          card.classList.add(
            'bb-card--disabled'
          );

          return;
        }

        const currentVariant =
          variants.find(
            (variant) => variant.available
          ) || variants[0];

        card._bbVariant = currentVariant;

        this.cardData.set(productId, {
          card,
          variants,
          quantityInput: null,
          quantityMinus: null,
          quantityPlus: null
        });

        this.initQuantityControls(
          card,
          productId
        );

        this.initCardToggle(
          card,
          productId
        );

        this.initVariantSelect(
          card,
          productId,
          variants
        );
      });
    }

    /**
     * Quantity controls are injected by JS so the
     * existing Liquid markup does not need to change.
     */
    initQuantityControls(card, productId) {
      let quantityWrap = card.querySelector(
        '[data-bb-quantity]'
      );

      if (!quantityWrap) {
        quantityWrap =
          document.createElement('div');

        quantityWrap.className =
          'bb-card__quantity';

        quantityWrap.setAttribute(
          'data-bb-quantity',
          ''
        );

        quantityWrap.setAttribute(
          'role',
          'group'
        );

        quantityWrap.setAttribute(
          'aria-label',
          'Quantity'
        );

        quantityWrap.innerHTML = `
          <button
            type="button"
            class="bb-card__quantity-button"
            data-bb-qty-minus
            aria-label="Decrease quantity"
          >−</button>

          <input
            type="number"
            class="bb-card__quantity-input"
            data-bb-qty-input
            value="0"
            min="0"
            max="${this.config.maxQty}"
            inputmode="numeric"
            aria-label="Quantity"
          >

          <button
            type="button"
            class="bb-card__quantity-button"
            data-bb-qty-plus
            aria-label="Increase quantity"
          >+</button>
        `;

        const info = card.querySelector(
          '.bb-card__info'
        );

        if (info) {
          info.appendChild(
            quantityWrap
          );
        } else {
          card.appendChild(
            quantityWrap
          );
        }
      }

      const input =
        quantityWrap.querySelector(
          '[data-bb-qty-input]'
        );

      const minus =
        quantityWrap.querySelector(
          '[data-bb-qty-minus]'
        );

      const plus =
        quantityWrap.querySelector(
          '[data-bb-qty-plus]'
        );

      if (!input || !minus || !plus) {
        return;
      }

      /*
       * Input event
       */
      input.addEventListener(
        'input',
        () => {
          /*
           * Allow an empty input temporarily while
           * the customer is editing.
           */
          if (input.value === '') return;

          this.setCardQuantity(
            card,
            productId,
            input.value
          );
        }
      );

      /*
       * Change event
       */
      input.addEventListener(
        'change',
        () => {
          this.setCardQuantity(
            card,
            productId,
            input.value || 0
          );
        }
      );

      /*
       * Blur event
       */
      input.addEventListener(
        'blur',
        () => {
          this.setCardQuantity(
            card,
            productId,
            input.value || 0
          );
        }
      );

      /*
       * Enter key
       */
      input.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter') return;

          event.preventDefault();

          this.setCardQuantity(
            card,
            productId,
            input.value || 0
          );

          input.blur();
        }
      );

      /*
       * Minus
       */
      minus.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          const current =
            this.getCardQuantity(
              productId
            );

          this.setCardQuantity(
            card,
            productId,
            current - 1
          );
        }
      );

      /*
       * Plus
       */
      plus.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          const current =
            this.getCardQuantity(
              productId
            );

          this.setCardQuantity(
            card,
            productId,
            current + 1
          );
        }
      );

      /*
       * Prevent quantity UI from triggering
       * the card/image toggle.
       */
      quantityWrap.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
        }
      );

      const data =
        this.cardData.get(productId);

      if (data) {
        data.quantityInput = input;
        data.quantityMinus = minus;
        data.quantityPlus = plus;
      }
    }

    initCardToggle(card, productId) {
      const toggle = card.querySelector(
        '[data-bb-toggle]'
      );

      if (!toggle) return;

      toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          this.toggleCard(
            card,
            productId
          );
        }
      );
    }

    initVariantSelect(
      card,
      productId,
      variants
    ) {
      const select = card.querySelector(
        '[data-bb-variant-select]'
      );

      if (!select) return;

      select.addEventListener(
        'change',
        () => {
          const variantId =
            Number(select.value);

          const variant =
            variants.find(
              (item) =>
                Number(item.id) ===
                variantId
            );

          if (!variant) return;

          card._bbVariant = variant;

          this.renderCardPrice(
            card,
            variant
          );

          const selectedItem =
            this.selected.get(
              productId
            );

          /*
           * Preserve quantity when variant
           * changes.
           */
          if (selectedItem) {
            selectedItem.variantId =
              Number(variant.id);

            selectedItem.price =
              Number(variant.price) || 0;

            this.selected.set(
              productId,
              selectedItem
            );
          }

          this.update();
        }
      );
    }

    initAddButton() {
      if (!this.addButton) return;

      this.addButton.addEventListener(
        'click',
        () => {
          this.addToCart();
        }
      );
    }

    /**
     * Clicking the product image toggles:
     *
     * 0 -> 1
     * any quantity -> 0
     */
    toggleCard(
      card,
      productId
    ) {
      const currentQuantity =
        this.getCardQuantity(
          productId
        );

      this.setCardQuantity(
        card,
        productId,
        currentQuantity > 0
          ? 0
          : 1
      );
    }

    getCardQuantity(productId) {
      const item =
        this.selected.get(
          productId
        );

      return item
        ? Number(item.quantity) || 0
        : 0;
    }

    /**
     * Normalize quantity and keep the
     * total bundle quantity within maxQty.
     */
    normalizeQuantity(
      quantity,
      productId
    ) {
      let value = Number(quantity);

      if (!Number.isFinite(value)) {
        value = 0;
      }

      value = Math.floor(value);

      value = Math.max(
        0,
        value
      );

      const currentQuantity =
        this.getCardQuantity(
          productId
        );

      const totalWithoutCurrent =
        this.getTotalQuantity() -
        currentQuantity;

      const availableForThisCard =
        Math.max(
          0,
          this.config.maxQty -
            totalWithoutCurrent
        );

      return Math.min(
        value,
        availableForThisCard
      );
    }

    setCardQuantity(
      card,
      productId,
      quantity
    ) {
      if (this.isAdding) return;

      const normalizedQuantity =
        this.normalizeQuantity(
          quantity,
          productId
        );

      const data =
        this.cardData.get(
          productId
        );

      if (!data) return;

      if (normalizedQuantity <= 0) {
        this.selected.delete(
          productId
        );
      } else {
        const variant =
          card._bbVariant;

        if (!variant) return;

        this.selected.set(
          productId,
          {
            variantId:
              Number(variant.id),

            price:
              Number(variant.price) || 0,

            quantity:
              normalizedQuantity
          }
        );
      }

      this.renderCardState(
        card,
        productId
      );

      this.update();
    }

    renderCardState(
      card,
      productId
    ) {
      const quantity =
        this.getCardQuantity(
          productId
        );

      const isSelected =
        quantity > 0;

      /*
       * Selected state
       */
      card.classList.toggle(
        'bb-card--selected',
        isSelected
      );

      /*
       * Toggle accessibility
       */
      const toggle =
        card.querySelector(
          '[data-bb-toggle]'
        );

      if (toggle) {
        toggle.setAttribute(
          'aria-pressed',
          String(isSelected)
        );

        const title =
          card.querySelector(
            '.bb-card__title'
          );

        const productTitle =
          title
            ? title.textContent.trim()
            : 'product';

        toggle.setAttribute(
          'aria-label',
          `${
            isSelected
              ? 'Remove'
              : 'Select'
          } ${productTitle}`
        );
      }

      const data =
        this.cardData.get(
          productId
        );

      if (!data) return;

      /*
       * Quantity input
       */
      if (data.quantityInput) {
        data.quantityInput.value =
          String(quantity);

        data.quantityInput.setAttribute(
          'aria-valuenow',
          String(quantity)
        );
      }

      /*
       * Minus button
       */
      if (data.quantityMinus) {
        data.quantityMinus.disabled =
          quantity <= 0 ||
          this.isAdding;
      }

      /*
       * Plus button
       */
      if (data.quantityPlus) {
        data.quantityPlus.disabled =
          this.isAdding ||
          this.getTotalQuantity() >=
            this.config.maxQty;
      }
    }

    renderAllCardStates() {
      this.cardData.forEach(
        (data, productId) => {
          this.renderCardState(
            data.card,
            productId
          );
        }
      );
    }

    renderCardPrice(
      card,
      variant
    ) {
      const priceEl =
        card.querySelector(
          '[data-bb-price]'
        );

      if (priceEl) {
        priceEl.textContent =
          this.formatMoney(
            variant.price
          );
      }

      const compareEl =
        card.querySelector(
          '[data-bb-compare]'
        );

      if (!compareEl) return;

      if (
        variant.compare_at_price &&
        Number(
          variant.compare_at_price
        ) >
          Number(variant.price)
      ) {
        compareEl.textContent =
          this.formatMoney(
            variant.compare_at_price
          );

        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    /**
     * Total number of physical units
     * currently selected.
     */
    getTotalQuantity() {
      let total = 0;

      this.selected.forEach(
        (item) => {
          total +=
            Number(item.quantity) || 0;
        }
      );

      return total;
    }

    /**
     * Returns the highest tier unlocked
     * for a given TOTAL unit count and
     * the next tier.
     */
    getTierState(count) {
      let unlocked = null;
      let next = null;

      this.config.tiers.forEach(
        (tier) => {
          const qty =
            Number(tier.qty) || 0;

          if (count >= qty) {
            unlocked = tier;
          } else if (!next) {
            next = tier;
          }
        }
      );

      return {
        unlocked,
        next
      };
    }

    update() {
      const count =
        this.getTotalQuantity();

      const state =
        this.getTierState(
          count
        );

      this.renderMessage(
        count,
        state
      );

      this.renderProgress(
        count
      );

      this.renderTotals(
        state
      );

      this.renderButton(
        count
      );

      this.renderAllCardStates();
    }

    renderMessage(
      count,
      state
    ) {
      if (!this.messageEl) return;

      const messages =
        this.config.messages || {};

      let text = '';

      if (state.next) {
        const remaining =
          Math.max(
            0,
            Number(state.next.qty) -
              count
          );

        const template =
          remaining === 1
            ? messages.single
            : messages.plural;

        text = String(
          template || ''
        )
          .split('[count]')
          .join(remaining)
          .split('[discount]')
          .join(state.next.off);
      } else if (
        state.unlocked
      ) {
        text = String(
          messages.unlocked || ''
        )
          .split('[discount]')
          .join(
            state.unlocked.off
          );
      }

      this.messageEl.textContent =
        text;

      this.messageEl.classList.toggle(
        'bb-message--unlocked',
        !state.next &&
          !!state.unlocked
      );
    }

    renderProgress(count) {
      /*
       * Each progress segment represents
       * one unit.
       */
      this.segments.forEach(
        (segment, index) => {
          segment.classList.toggle(
            'bb-progress__segment--filled',
            index < count
          );
        }
      );

      /*
       * Tier badges
       */
      this.badges.forEach(
        (badge) => {
          const qty =
            Number(
              badge.getAttribute(
                'data-bb-badge'
              )
            ) || 0;

          badge.classList.toggle(
            'bb-progress__badge--active',
            count >= qty
          );
        }
      );
    }

    renderTotals(state) {
      let subtotal = 0;

      /*
       * price × quantity
       */
      this.selected.forEach(
        (item) => {
          subtotal +=
            (Number(item.price) || 0) *
            (Number(item.quantity) || 0);
        }
      );

      const discountPercent =
        state.unlocked
          ? Number(
              state.unlocked.off
            ) || 0
          : 0;

      const finalTotal =
        Math.round(
          subtotal *
            (100 - discountPercent) /
            100
        );

      const savings =
        Math.max(
          0,
          subtotal - finalTotal
        );

      /*
       * Original total
       */
      if (this.totalOriginalEl) {
        this.totalOriginalEl.textContent =
          this.formatMoney(
            subtotal
          );

        this.totalOriginalEl.classList.toggle(
          'bb-total__original--struck',
          savings > 0
        );
      }

      /*
       * Final total
       */
      if (this.totalFinalEl) {
        this.totalFinalEl.textContent =
          this.formatMoney(
            finalTotal
          );
      }

      /*
       * Savings
       */
      if (!this.savingsEl) return;

      if (savings > 0) {
        this.savingsEl.hidden =
          false;

        this.savingsEl.textContent =
          String(
            this.config.messages
              .savings || ''
          )
            .split('[amount]')
            .join(
              this.formatMoney(
                savings
              )
            );
      } else {
        this.savingsEl.hidden =
          true;

        this.savingsEl.textContent =
          '';
      }
    }

    renderButton(count) {
      if (!this.addButton) return;

      const ready =
        count >=
          this.config.minQty &&
        count <=
          this.config.maxQty &&
        !this.isAdding;

      this.addButton.disabled =
        !ready;

      this.addButton.classList.toggle(
        'bb-add--ready',
        ready
      );

      if (
        !this.addButtonLabel ||
        this.isAdding
      ) {
        return;
      }

      let label =
        this.config.messages
          .button || '';

      if (
        this.config.messages
          .buttonCount
      ) {
        label += ` (${count})`;
      }

      this.addButtonLabel.textContent =
        label;
    }

    applyDiscountCode(count) {
      const state =
        this.getTierState(
          count
        );

      const code =
        state.unlocked &&
        state.unlocked.code
          ? state.unlocked.code
          : '';

      if (!code) {
        return Promise.resolve();
      }

      const url =
        this.config.discountPath.replace(
          '[code]',
          encodeURIComponent(code)
        );

      return fetch(url, {
        credentials:
          'same-origin'
      }).catch(() => {
        /*
         * Discount application is
         * intentionally non-blocking.
         */
      });
    }

    addToCart() {
      const totalQuantity =
        this.getTotalQuantity();

      /*
       * Validate bundle quantity.
       */
      if (
        this.isAdding ||
        totalQuantity <
          this.config.minQty ||
        totalQuantity >
          this.config.maxQty
      ) {
        return;
      }

      this.isAdding = true;

      this.update();

      if (this.addButtonLabel) {
        this.addButtonLabel.textContent =
          this.config.messages
            .adding ||
          'Adding...';
      }

      /*
       * Build Shopify cart items.
       *
       * Example:
       *
       * Product A × 2
       * Product B × 3
       *
       * becomes:
       *
       * [
       *   {
       *     id: 123,
       *     quantity: 2
       *   },
       *   {
       *     id: 456,
       *     quantity: 3
       *   }
       * ]
       */
      const items = [];

      this.selected.forEach(
        (item) => {
          const quantity =
            Number(
              item.quantity
            ) || 0;

          if (quantity > 0) {
            items.push({
              id: Number(
                item.variantId
              ),
              quantity
            });
          }
        }
      );

      /*
       * Safety check.
       */
      if (!items.length) {
        this.handleAddError(
          new Error(
            'Bundle is empty.'
          )
        );

        return;
      }

      /*
       * Apply tier discount first.
       */
      this.applyDiscountCode(
        totalQuantity
      )
        .then(() => {
          return fetch(
            this.config.routes
              .cartAdd,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json'
              },

              credentials:
                'same-origin',

              body: JSON.stringify({
                items
              })
            }
          );
        })

        /*
         * Handle response.
         */
        .then((response) => {
          if (!response.ok) {
            return response
              .json()
              .catch(() => ({}))
              .then((error) => {
                throw error;
              });
          }

          return response.json();
        })

        /*
         * Success.
         */
        .then(() => {
          this.isAdding =
            false;

          if (this.addButton) {
            this.addButton.classList.remove(
              'bb-add--loading'
            );
          }

          this.afterAdd();

          this.update();
        })

        /*
         * Error.
         */
        .catch((error) => {
          this.handleAddError(
            error
          );
        });
    }

    handleAddError(error) {
      console.error(
        'BundleBuilder: could not add bundle to cart',
        error
      );

      this.isAdding = false;

      if (this.addButton) {
        this.addButton.classList.remove(
          'bb-add--loading'
        );
      }

      if (this.addButtonLabel) {
        this.addButtonLabel.textContent =
          this.config.messages
            .error ||
          'Error — try again';
      }

      this.renderAllCardStates();

      /*
       * Restore normal button state.
       */
      window.setTimeout(() => {
        this.update();
      }, 2000);
    }

    afterAdd() {
      const goToCart =
        this.config.redirectToCart ||
        this.config.cartType !==
          'drawer';

      /*
       * Redirect to cart page.
       */
      if (goToCart) {
        window.location.href =
          this.config.routes
            .cartPage;

        return;
      }

      /*
       * Open/update theme cart drawer.
       *
       * Keep the existing theme event
       * so the current cart drawer
       * integration continues working.
       */
      document.dispatchEvent(
        new CustomEvent(
          'ajaxProduct:added',
          {
            bubbles: true,

            detail: {
              source:
                'bundle-builder'
            }
          }
        )
      );
    }

    formatMoney(cents) {
      if (
        window.theme &&
        theme.Currency &&
        typeof theme.Currency
          .formatMoney ===
          'function'
      ) {
        return theme.Currency.formatMoney(
          cents,
          this.config.moneyFormat
        );
      }

      return this.fallbackMoney(
        cents
      );
    }

    fallbackMoney(cents) {
      const format =
        this.config.moneyFormat ||
        '${{amount}}';

      function placeholderRegex(
        name
      ) {
        return new RegExp(
          '\\{\\{\\s*' +
            name +
            '\\s*\\}\\}'
        );
      }

      function withDelimiters(
        number,
        precision,
        thousands,
        decimal
      ) {
        precision =
          precision == null
            ? 2
            : precision;

        thousands =
          thousands || ',';

        decimal =
          decimal || '.';

        const parts =
          Number(number)
            .toFixed(precision)
            .split('.');

        parts[0] =
          parts[0].replace(
            /\B(?=(\d{3})+(?!\d))/g,
            thousands
          );

        return parts.length > 1
          ? parts.join(decimal)
          : parts[0];
      }

      let value;

      /*
       * No decimals + comma
       */
      if (
        placeholderRegex(
          'amount_no_decimals_with_comma_separator'
        ).test(format)
      ) {
        value =
          withDelimiters(
            cents / 100,
            0,
            '.',
            ','
          );
      }

      /*
       * Decimal + comma
       */
      else if (
        placeholderRegex(
          'amount_with_comma_separator'
        ).test(format)
      ) {
        value =
          withDelimiters(
            cents / 100,
            2,
            '.',
            ','
          );
      }

      /*
       * No decimals
       */
      else if (
        placeholderRegex(
          'amount_no_decimals'
        ).test(format)
      ) {
        value =
          withDelimiters(
            cents / 100,
            0
          );
      }

      /*
       * Decimal + space
       */
      else if (
        placeholderRegex(
          'amount_with_space_separator'
        ).test(format)
      ) {
        value =
          withDelimiters(
            cents / 100,
            2,
            ' ',
            '.'
          );
      }

      /*
       * Decimal + apostrophe
       */
      else if (
        placeholderRegex(
          'amount_with_apostrophe_separator'
        ).test(format)
      ) {
        value =
          withDelimiters(
            cents / 100,
            2,
            "'",
            '.'
          );
      }

      /*
       * Default
       */
      else {
        value =
          withDelimiters(
            cents / 100,
            2
          );
      }

      return format.replace(
        /\{\{\s*\w+\s*\}\}/,
        value
      );
    }
  }

  customElements.define(
    'bundle-builder',
    BundleBuilder
  );
})();